/**
 * Shared deal sort options for the board (Kanban) and list views.
 *
 * Each option encodes a column + direction in a single dropdown value so
 * the UI stays a plain <select>. "Last activity: oldest first" is the
 * answer to "show me the leads I haven't contacted recently" — it surfaces
 * never-contacted deals (NULL last_activity) at the top.
 */

import type { FiasLeadsDealSortBy } from '@/lib/api';

export interface DealSortOption {
  value: string;
  label: string;
  sortBy: FiasLeadsDealSortBy;
  sortDir: 'asc' | 'desc';
}

export const DEAL_SORT_OPTIONS: DealSortOption[] = [
  { value: 'updated_at:desc', label: 'Recently updated', sortBy: 'updated_at', sortDir: 'desc' },
  {
    value: 'last_activity_at:asc',
    label: 'Last activity: oldest first',
    sortBy: 'last_activity_at',
    sortDir: 'asc',
  },
  {
    value: 'last_activity_at:desc',
    label: 'Last activity: newest first',
    sortBy: 'last_activity_at',
    sortDir: 'desc',
  },
  { value: 'value:desc', label: 'Value: high → low', sortBy: 'value', sortDir: 'desc' },
  { value: 'value:asc', label: 'Value: low → high', sortBy: 'value', sortDir: 'asc' },
  {
    value: 'expected_close_date:asc',
    label: 'Close date: soonest',
    sortBy: 'expected_close_date',
    sortDir: 'asc',
  },
  { value: 'name:asc', label: 'Name: A → Z', sortBy: 'name', sortDir: 'asc' },
];

export const DEFAULT_DEAL_SORT = DEAL_SORT_OPTIONS[0];

export function parseDealSort(value: string): DealSortOption {
  return DEAL_SORT_OPTIONS.find((o) => o.value === value) ?? DEFAULT_DEAL_SORT;
}
